import * as React from 'react';
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useContent } from "../../Content/cms";
import { PopUpSuccess, getEmptyValue, participant2Values } from "../../Commons/Helpers";
import { useFormFields } from '../../Commons/Components/FormFields';
import Apirest from "../../Content/Apirest";
import { loadAvailableTags } from '../../Commons/Utils/tags';
import { useFeatureToogle } from '../../ProtectedRoutes/FeatureToggle/hook';

// Import interface
import { Locale, Participant } from "../interfaces";
import { FormSectionScheme, FieldScheme, FormSubmitData, Option } from '../../Commons/Components/FormFields/types';
import { ADD_OR_UPDATE_PARTICIPANT } from "../../store/participants/types";
import { StoreState } from '../../store';

// Import components
import FeatureToggle from '../../ProtectedRoutes/FeatureToggle';
import ParticipantHeader from "../../Commons/Components/Participant/Header";
import Container from "react-bootstrap/Container";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import { Badge, InputGroup, Accordion, Card } from "react-bootstrap";
import {RowSpinner} from "../../Commons/MultipleComponents";
import {FormField} from "../../Commons/Components/FormFields/Field";

import GoBackButton from '../../Commons/Components/GoBackButton';

// Load form schema
import form_schema from './form_schema.json';

//import css
import "./index.css";


const EditParticipant : React.FC = () => {

    let getContent = useContent();

    let {featureEnabled} = useFeatureToogle();

    let today= new Date().toISOString().split("T")[0];

    let {participant_id} = useParams<{participant_id: string}>();
    let participant = useSelector<StoreState, Participant>(
        (state: StoreState) => state.participants.find(p => p.id.toString() === participant_id)!,
        shallowEqual
    );

    const mobiles = require('is-mobile');
    let mobile = mobiles();

    const {
        sections,
        setSections,
        setFormValues,
        submitHandler,
        onChange,
        state
    } = useFormFields(true, false);

    const [assignedTags, setAssignedTags] = useState<string[]>([]);
    const [createTagValue, setCreateTagValue] = useState<string>('');
    const [availableTags, setAvailableTags] = useState<string[]>([]);

    function addTag(tag_name: string) {
        tag_name = tag_name.toLowerCase();
        setAssignedTags(prev => {
            if (prev.find(t => t === tag_name) === undefined) {
                return [...prev, tag_name];
            }
            return prev;
        });
    }

    function createTag(e: any) {
        if (e && e.preventDefault) e.preventDefault();
        let tag_name = (createTagValue || '').toString().trim().toLowerCase();
        if (tag_name !== '') {
            addTag(tag_name);
            setAvailableTags(prev => prev.find(t => t === tag_name) ? prev : [...prev, tag_name]);
            // Persist immediately when editing an existing participant
            const newAssigned = (() => {
                // ensure assignedTags updated synchronously for API call
                const exists = assignedTags.find(t => t === tag_name);
                return exists ? assignedTags : [...assignedTags, tag_name];
            })();

            if (participant && participant_id) {
                Apirest.update_participant(
                    participant_id,
                    { tags: newAssigned },
                    (resp: Participant) => {
                        // update store so UI stays in sync
                        updateParticipant(resp);
                    },
                    (err: any) => {
                        // silent fail: keep UI responsive and log error
                        console.error('Failed to persist tag on edit:', err);
                    }
                );
            }

            setCreateTagValue('');
        }
    }

    function removeTag(tag_name: string) {
        tag_name = tag_name.toLowerCase();
        setAssignedTags(prev => prev.filter(t => t !== tag_name));
    }

    let dispatch = useDispatch();
    const updateParticipant = (participant: Participant) => dispatch({
        type: ADD_OR_UPDATE_PARTICIPANT,
        payload: participant
    });

    useEffect(
        () => {
            const new_sections : FormSectionScheme[] = [{
                name: '',
                description: '',
                fields: form_schema as FieldScheme[]
            }];

            let sex_field = new_sections[0].fields.find(field => field.name === 'sex');
            
            if (sex_field !== undefined && sex_field.option_values !== undefined) {
                sex_field.options = sex_field.option_values.map(
                    option => ({
                        value: option,
                        label: getContent('edit_participant__form__sex__options__' + option)
                    })
                )
            }

            // Ad max date on diagnose date and birthday fields
            for (let section of new_sections) {
                for (let field of section.fields) {
                    if (field.name === "diagnose_date") {
                        field.max = today;
                    } else if (field.name === "birthday"){
                        field.max = today;
                    }
                }
            }
            
            for (let section of new_sections) {
                for (let field of section.fields) {
                    if (field.name !== 'sex' && typeof field.options !== 'undefined') {
                        field.options = field.options.map((option: Option) => ({
                            ...option,
                            label: getContent(option.label)
                        }));
                    }
                }
            }

            // Add locales to the form
            Apirest.get_locales((response: Locale[]) => {
                for (let section of new_sections) {
                    for (let field of section.fields) {
                        if (field.name === 'locale' && field.options === undefined) {
                            field.options = response.map(locale => ({
                                value: locale.id.toString(),
                                label: locale.code
                            }));           
                        }
                    }
                }

                setSections(new_sections);
            });
        },
        [] // eslint-disable-line react-hooks/exhaustive-deps
    );

    useEffect(
        () => {
            if (participant) {
                setFormValues(participant2Values(participant));
                setAssignedTags(participant.tags || []);
            }
        },
        [participant] // eslint-disable-line react-hooks/exhaustive-deps
    );

    useEffect(() => {
        // Load available tags using shared helper
        loadAvailableTags((tags: string[]) => setAvailableTags(tags));
    }, []);

    function onSubmit(data: FormSubmitData) {
        let promise = new Promise<void>(
            (resolve, reject) => {
                const payload = {
                    ...data,
                    tags: assignedTags
                };

                Apirest.update_participant(
                    participant_id,
                    payload,
                    (response: Participant) => {
                        updateParticipant(response);
        
                        PopUpSuccess(
                            getContent("edit_participant__server_response__edit_participant_info__success")
                        );

                        resolve();
                    },
                    (jqXHR: JQueryXHR) => {
                        reject(jqXHR.responseJSON);
                    }
                );
            }
        );

        return promise;
    }

    if (sections.length === 0) {
        return <RowSpinner show className='mt-3'/>;
    }

    return (
        <Container fluid >
            <ParticipantHeader />
            <Row className={'bg-info p-2 d-flex justify-content-between'}>
            <Col className={'mt-2'}>
                <h5 className={'text-white  font-weight-bold mb-0 pb-0'}>
                    {getContent("edit_participant__title")}
                </h5>
            </Col>
            <GoBackButton/>
            </Row>
            <Row className={'bg-light'}> 
                <div className={'content-minheight w-100 px-5'} >
                    <Form onSubmit={submitHandler(onSubmit)}>
                        <Form.Group>
                            <Row >
                                {sections.map(({fields},rdx) =>
                                    <Col key={rdx} className={'px-3 my-3 ' + (mobile ? null : 'form-container')}>
                                            {fields.map((field, fdx) =>
                                                <FormField
                                                    key={fdx}
                                                    {...field}
                                                    serverErrors={state.errors[field.name]}
                                                    value={state.values[field.name] || getEmptyValue(field.type)}
                                                    onChange={onChange}
                                                    useCMS={true}
                                                    disabled={!featureEnabled(['editParticipant'])}
                                                />
                                         )}
                                    </Col>
                                )}
                            </Row>

                            <Row className={'my-3'}>
                                <Col>
                                    <Accordion>
                                        <Card>
                                            <Accordion.Toggle as={Card.Header} className='cursor' eventKey="0">
                                                <p className='pb-0 mb-0'>{getContent('add_tag__select_available_tag__title')}</p>
                                            </Accordion.Toggle>
                                            <Accordion.Collapse eventKey="0">
                                                <Card.Body>
                                                    {availableTags.map((tag, i) =>
                                                        assignedTags.includes(tag)
                                                            ? null :
                                                            <Badge
                                                                key={i.toString()}
                                                                className={'mr-1 ml-0 cursor badge-tags'}
                                                                onClick={() => addTag(tag)}
                                                            >{tag}</Badge>
                                                    )}
                                                </Card.Body>
                                            </Accordion.Collapse>
                                        </Card>
                                    </Accordion>
                                    <InputGroup className={'mt-2'}>
                                        <Col sm={6} className='m-0 p-0 d-flex justify-content-center'>
                                            <Form.Control
                                                type={'text'}
                                                placeholder={getContent('add_tag__create__form__text_placeholder')}
                                                value={createTagValue}
                                                onChange={(e) => setCreateTagValue(e.target.value)}
                                            />
                                        </Col>
                                        <Col className='m-0 d-flex justify-content-center'>
                                            <Button type={'button'} onClick={createTag} className={'create-tag-button pr-0'} size={'sm'}>{getContent('add_tag__create__form__button')}</Button>
                                        </Col>
                                    </InputGroup>
                                </Col>
                                <Col>
                                    <div className={'d-flex flex-wrap'}>
                                        {assignedTags.length > 0 ? assignedTags.map((t, i) => (
                                            <div key={i.toString()} className={'d-flex align-items-center mr-3 mt-2'}>
                                                <Badge className={'badge-tags'}>{t}</Badge>
                                                <Button variant={'link'} onClick={() => removeTag(t)} className={'p-0 ml-1'}>x</Button>
                                            </div>
                                        )) : <div className={'bg-light p-3 text-center'}>{getContent('admin__form__tags_assigned__empty') || 'No hay tags asignados'}</div>}
                                    </div>
                                </Col>
                            </Row>
                            <FeatureToggle featureNames={['editParticipant']}>
                                <Row xs={12} sm={6} className={'justify-content-center mt-3'} >
                                    <Button className={'m-3 col-9'} variant={'primary'} type={'submit'} block disabled={state.sending}>
                                        {!state.sending && getContent('edit_participant__form__submit_button')}
                                        {state.sending && <Spinner animation={'border'} size={'sm'} color={'#FFFFFF'} />}
                                    </Button>
                                </Row>
                            </FeatureToggle>
                        </Form.Group>
                    </Form>
                </div>
            </Row>
        </Container>
    );
};


export default EditParticipant;
